import type { Phase } from '@concord/domain';
import { isBuilt, PHASE_NAME } from '@concord/domain';
import type { ReactNode } from 'react';
import { cn } from '../cn';
import { Icon } from '../icons';
import { Menu, type MenuAction } from '../overlays';
import { BuildStamp } from './build-stamp';
import { useHeaderActions } from './header-actions';
import { useLink } from './link';
import s from './nav-shell.module.css';
import { RailContext, useRail, useRailState } from './rail-state';

export interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  to: string;
  count?: number | null;
  phase?: Phase;
  /** Shown only to accounts holding this permission (the audit log is admin-only). */
  requires?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** The console's rail as in the prototype. Later-phase items are inert and marked. */
export const CONSOLE_NAV: NavGroup[] = [
  {
    label: 'Work',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: <Icon.Dashboard className={s.ico} />, to: '/console' },
      { key: 'cases', label: 'Cases', icon: <Icon.Cases className={s.ico} />, to: '/console/cases' },
      {
        key: 'directory',
        label: 'Directory',
        icon: <Icon.Directory className={s.ico} />,
        to: '/console/directory',
      },
      { key: 'panels', label: 'Panels', icon: <Icon.Panels className={s.ico} />, to: '/console/panels' },
      {
        key: 'consultations',
        label: 'Consultations',
        icon: <Icon.Consultations className={s.ico} />,
        to: '/console/consultations',
      },
    ],
  },
  {
    label: 'Money',
    items: [
      { key: 'payables', label: 'Payables', icon: <Icon.Payables className={s.ico} />, to: '#', phase: 'P3' },
      { key: 'reports', label: 'Reports', icon: <Icon.Reports className={s.ico} />, to: '#', phase: 'P5' },
    ],
  },
  {
    label: 'Setup',
    items: [
      {
        key: 'plans',
        label: 'Customers & plans',
        icon: <Icon.Customers className={s.ico} />,
        to: '/console/plans',
        requires: 'directory.view',
      },
      {
        key: 'pools',
        label: 'Physician pools',
        icon: <Icon.Pools className={s.ico} />,
        to: '/console/pools',
        requires: 'directory.view',
      },
    ],
  },
];

export function Rail({
  groups,
  current,
  counts = {},
  footer,
  permissions = [],
}: {
  groups: NavGroup[];
  current: string;
  counts?: Record<string, number | null | undefined>;
  footer?: ReactNode;
  /** The signed-in account's permissions; items that require one they lack are left out. */
  permissions?: readonly string[];
}) {
  const Link = useLink();
  const { collapsed, toggle } = useRail();
  return (
    <aside id="console-rail" className={s.rail} aria-label="Console" data-collapsed={collapsed || undefined}>
      <div className={s.brand}>
        {!collapsed && (
          <>
            <span className={s.mark}>Concord</span>
            <span className={s.dot} />
            <span className={s.sub}>v0.1</span>
          </>
        )}
        <button
          type="button"
          className={s.toggle}
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls="console-rail"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Icon.RailToggle className={s.ico} />
        </button>
      </div>
      {groups.map((g) => (
        <nav key={g.label} className={s.navgroup} aria-label={g.label}>
          <span className={s.lbl}>{g.label}</span>
          {g.items
            .filter((item) => !item.requires || permissions.includes(item.requires))
            .map((item) => {
              const inert = item.phase && !isBuilt(item.phase);
              if (inert) {
                return (
                  <span
                    key={item.key}
                    className={s.nav}
                    aria-disabled="true"
                    title={`${collapsed ? `${item.label}. ` : ''}Arrives in Phase ${item.phase!.slice(1)}: ${PHASE_NAME[item.phase!]}`}
                  >
                    {item.icon}
                    <span className={s.label}>{item.label}</span>
                    <span className={s.phase}>{item.phase}</span>
                  </span>
                );
              }
              const n = counts[item.key];
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={s.nav}
                  aria-current={current === item.key ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                >
                  {item.icon}
                  <span className={s.label}>{item.label}</span>
                  {n != null && <span className={s.count}>{n.toLocaleString('en-US')}</span>}
                </Link>
              );
            })}
        </nav>
      ))}
      <div className={s['rail-foot']}>{collapsed ? 'P1' : (footer ?? 'Phase 1')}</div>
    </aside>
  );
}

export function TopBar({
  search,
  user,
  menu,
}: {
  search: ReactNode;
  user: { initials: string; name: string; role: string };
  /** The profile menu under the name: sign-in security, admin pages, sign out. */
  menu?: MenuAction[];
}) {
  const actions = useHeaderActions();
  const who = (
    <>
      <span className={s.av}>{user.initials}</span>
      <div>
        <div className={s.nm}>{user.name}</div>
        <div className={s.rl}>{user.role}</div>
      </div>
    </>
  );
  return (
    <div className={s.topbar}>
      {search}
      {actions && <div className={s.hactions}>{actions}</div>}
      {menu?.length ? (
        <Menu
          actions={menu}
          trigger={
            <button
              type="button"
              className={cn(s.who, s.whobtn)}
              aria-label={`Profile menu for ${user.name}`}
            >
              {who}
              <Icon.ChevronDown className={s.caret} />
            </button>
          }
        />
      ) : (
        <div className={s.who}>{who}</div>
      )}
    </div>
  );
}

/** The top-bar search box (`/` focuses it). */
export function SearchBox({
  value,
  onChange,
  onSubmit,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <search style={{ display: 'contents' }}>
      <form
        className={s.search}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Icon.Search />
        <input
          ref={inputRef}
          aria-label="Search cases, people, reference numbers"
          placeholder="Search cases, people, reference numbers"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className={s.kbd}>/</span>
      </form>
    </search>
  );
}

export function NavShell({
  rail,
  topbar,
  children,
  roomy,
}: {
  rail: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
  /** The dashboard's slightly roomier spacing. */
  roomy?: boolean;
}) {
  const railState = useRailState();
  return (
    <RailContext.Provider value={railState}>
      <div className={s.app} data-rail={railState.collapsed ? 'collapsed' : 'expanded'}>
        {rail}
        <div>
          {topbar}
          <main className={cn(s.main, roomy && s.roomy)}>
            {children}
            <BuildStamp />
          </main>
        </div>
      </div>
    </RailContext.Provider>
  );
}

export function PageHead({
  title,
  when,
  actions,
  roomy,
}: {
  title: ReactNode;
  when?: ReactNode;
  actions?: ReactNode;
  roomy?: boolean;
}) {
  return (
    <div className={cn(s.pagehead, roomy && s.roomy)}>
      <div>
        <h1>{title}</h1>
        {when && <p className={s.when}>{when}</p>}
      </div>
      {actions && <div className={s.actions}>{actions}</div>}
    </div>
  );
}

export interface Crumb {
  label: ReactNode;
  to?: string;
  isRef?: boolean;
}

export function Crumbs({ items }: { items: Crumb[] }) {
  const Link = useLink();
  return (
    <nav className={s.crumbs} aria-label="Breadcrumb">
      {items.map((c, i) => (
        <span key={typeof c.label === 'string' ? c.label : i} style={{ display: 'contents' }}>
          {i > 0 && <span aria-hidden="true">›</span>}
          {c.to ? (
            <Link to={c.to} className={c.isRef ? 'ref' : undefined}>
              {c.label}
            </Link>
          ) : (
            <span className={c.isRef ? 'ref' : undefined}>{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
